// Day-execution ordering: your manual override layered on top of a deterministic
// 6-tier pipeline.  Final order = `manualOrder ?? computedRank`, so a task you
// hand-placed (voice/drag) lands at its number and everything else fills in around
// it by the systematic order. Pure — no Firestore I/O. Reused by mobile + web.

import { taskPriorityWeight } from './priority.js';

// Compare two hierarchical index labels ("1.2", "1.10", "2.1") numerically segment-by-segment.
function compareIndexLabels(a, b) {
  if (a === b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i] ?? 0;
    const y = pb[i] ?? 0;
    if (x !== y) return x - y;
  }
  return 0;
}

function orderVal(t) {
  const so = t.sortOrder;
  return (so === undefined || so === null) ? (t.createdAt || 0) : so;
}

/**
 * The deterministic comparator (tiers top→bottom). Not exported — drives the
 * computed rank used as the fallback for tasks with no manualOrder.
 * ctx: { today, blocksByProject: Map<projectId, startHour>, indices: Map<taskId,label>, doneIds: Set,
 *        outcomeTaskIds?: Set — tasks that feed an open outcome this week }
 */
function computeCompare(a, b, ctx) {
  const { today, blocksByProject, indices, doneIds, outcomeTaskIds } = ctx;

  // 1. Background/kickoff tasks first (passive, run while you work).
  const ab = a.isBackground ? 0 : 1;
  const bb = b.isBackground ? 0 : 1;
  if (ab !== bb) return ab - bb;

  // 2. Project block time — task inherits its project's earliest block start today; none = last.
  const at = (blocksByProject && blocksByProject.get(a.projectId)) ?? 99;
  const bt = (blocksByProject && blocksByProject.get(b.projectId)) ?? 99;
  if (at !== bt) return at - bt;

  // 3. Dependencies — a task whose prerequisite isn't done is held until ready.
  const aBlocked = a.blockedByTaskId && doneIds && !doneIds.has(a.blockedByTaskId) ? 1 : 0;
  const bBlocked = b.blockedByTaskId && doneIds && !doneIds.has(b.blockedByTaskId) ? 1 : 0;
  if (aBlocked !== bBlocked) return aBlocked - bBlocked;

  // 3b. This week's outcomes — a step that feeds an open outcome goes before the rest of its project.
  if (outcomeTaskIds && outcomeTaskIds.size) {
    const ao = outcomeTaskIds.has(a.id) ? 0 : 1;
    const bo = outcomeTaskIds.has(b.id) ? 0 : 1;
    if (ao !== bo) return ao - bo;
  }

  // 4. Phase/index order within a project.
  const ic = compareIndexLabels(indices ? indices.get(a.id) : '', indices ? indices.get(b.id) : '');
  if (ic !== 0) return ic;

  // 5. Priority + overdue boost.
  const pw = taskPriorityWeight(b, today) - taskPriorityWeight(a, today);
  if (pw !== 0) return pw;

  // 6. Cash nudge — billable / money-earning floats up.
  const am = (a.moneyEarned || 0) + (a.actualValue || 0);
  const bm = (b.moneyEarned || 0) + (b.actualValue || 0);
  if ((am > 0) !== (bm > 0)) return am > 0 ? -1 : 1;

  // Fallback: stable by sortOrder/createdAt.
  return orderVal(a) - orderVal(b);
}

/**
 * Resolve the day-execution order for a set of tasks.
 * @param {Array} tasks
 * @param {Object} ctx { today, blocksByProject?, indices?, doneIds? }
 * @returns {Array} ordered tasks. manualOrder (when set) beats the computed pipeline.
 */
export function resolveDayOrder(tasks = [], ctx = {}) {
  // Rank by the deterministic pipeline first.
  const computed = [...tasks].sort((a, b) => computeCompare(a, b, ctx));
  const rank = new Map();
  computed.forEach((t, i) => rank.set(t.id, i));

  // Effective key: your manual number if set, else computed rank. On a tie a manual
  // pin wins (so a bumped task beats a same-keyed computed one), then computed rank.
  return [...tasks].sort((a, b) => {
    const aHas = a.manualOrder !== undefined && a.manualOrder !== null;
    const bHas = b.manualOrder !== undefined && b.manualOrder !== null;
    const ka = aHas ? a.manualOrder : rank.get(a.id);
    const kb = bHas ? b.manualOrder : rank.get(b.id);
    if (ka !== kb) return ka - kb;
    if (aHas !== bHas) return aHas ? -1 : 1;
    return rank.get(a.id) - rank.get(b.id);
  });
}

/** True if the day's tasks have any manual override in effect. */
export function hasManualOrder(tasks = []) {
  return tasks.some((t) => t.manualOrder !== undefined && t.manualOrder !== null);
}
